import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import EntityMemberQuery, {
  ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH,
} from "_shared/entity-members/_api/queries/entity-member-query";
import useInfiniteEntityMembers from "_shared/entity-members/_hooks/use-infinite-entity-members";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import React, { useMemo } from "react";
import { useSession } from "users/__session/session-context";

const SEARCH_DEBOUNCE_MILLISECONDS = 300;
/** Why a member's row cannot be chosen: a leader is handed back as an individual, which needs both. */
const LEADER_UNCHOOSABLE_REASON = "Cannot lead: no marketplace profile with a name";

const PopoverContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(() => ({
  width: "100%",
})) as typeof TextField;
const LoaderRow = styled(ListItem)(() => ({
  justifyContent: "center",
}));
const ErrorRow = styled(ListItem)(({ theme }) => ({
  gap: theme.spacing(1),
  justifyContent: "center",
}));
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

/** One person the popover lists. `individual` is undefined for a member who cannot be chosen. */
type LeaderChoice = {
  userId: Guid;
  name: string;
  email?: string;
  individual?: Individual;
};

type LeaderChoiceRowProps = {
  choice: LeaderChoice;
  isSelected: boolean;
  onChoose: (leader: Individual) => void;
};

function LeaderChoiceRow(props: Readonly<LeaderChoiceRowProps>) {
  const { choice, isSelected, onChoose } = props;
  const individual = choice.individual;

  return (
    <ListItemButton
      disableGutters
      selected={isSelected}
      disabled={!individual}
      onClick={individual ? () => onChoose(individual) : undefined}
    >
      <ListItemAvatar>
        {isSelected ? (
          <SelectedIcon>
            <DoneIcon />
          </SelectedIcon>
        ) : (
          <IndividualAvatar individual={individual} disabled={!individual} />
        )}
      </ListItemAvatar>
      <ListItemText primary={choice.name} secondary={individual ? choice.email : LEADER_UNCHOOSABLE_REASON} />
    </ListItemButton>
  );
}

type TeamLeaderSelectorProps = {
  teamLeaderUserId?: Guid;
  popoverAnchor?: HTMLElement;
  onTeamLeaderUpdated?: (newTeamLeader?: Individual) => void;
  onPopoverClose?: () => void;
};

export default function TeamLeaderSelector(props: Readonly<TeamLeaderSelectorProps>) {
  const { teamLeaderUserId, popoverAnchor, onTeamLeaderUpdated, onPopoverClose } = props;
  const isOpen = popoverAnchor !== undefined;

  // #region State
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState<string>("");

  // #region Hooks
  const session = useSession();
  // The popover mounts the list after this component's effects have run, so the list is held in state
  // and the scroll hook is handed a new ref object once it mounts. A plain ref would stay empty when
  // the hook attached its listener. Memoized on purpose: the object is a dependency of that hook.
  const [listElement, setListElement] = React.useState<HTMLUListElement | null>(null);
  const listRef = useMemo(() => ({ current: listElement }), [listElement]);

  React.useEffect(() => {
    const handle = globalThis.setTimeout(
      () => setDebouncedSearchTerm(searchTerm.trim().slice(0, ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH)),
      SEARCH_DEBOUNCE_MILLISECONDS,
    );
    return () => globalThis.clearTimeout(handle);
  }, [searchTerm]);

  // #region Queries
  // Memoized on purpose: the query reaches the roster hook's effects, so a new object on every
  // render would restart them whether or not the compiler memoizes this component.
  const query = useMemo(() => new EntityMemberQuery({ query: debouncedSearchTerm }), [debouncedSearchTerm]);
  // The roster loads only while the popover is open. It includes the caller and the principal.
  const { members, isLoading, isInitialLoading, error, loadMore, retry } = useInfiniteEntityMembers(
    session.user?.legalEntityId ?? Guid.EMPTY,
    query,
    { skip: !session.user || !isOpen },
  );
  useInfiniteScroll(loadMore, members.hasNextPage, isLoading, listRef);

  const leaderIsOnLoadedPage =
    teamLeaderUserId !== undefined && members.data.some((member) => member.userId.isEqualTo(teamLeaderUserId));

  // The chosen leader stays in view while their page of the roster has not loaded, so it is read on
  // its own and shown first.
  const leaderQuery = useGetIndividualsByUserIdsQuery(
    isOpen && teamLeaderUserId && !leaderIsOnLoadedPage ? { userIds: [teamLeaderUserId.value] } : skipToken,
  );

  const pinnedLeader: LeaderChoice | undefined = (() => {
    if (!teamLeaderUserId || leaderIsOnLoadedPage || !leaderQuery.data) return undefined;
    const leader = Individual.fromJSONArray(leaderQuery.data)?.find((individual) =>
      individual.userId?.isEqualTo(teamLeaderUserId),
    );
    if (!leader) return undefined;
    return { userId: teamLeaderUserId, name: leader.fullName, email: leader.email?.value, individual: leader };
  })();

  const choices: LeaderChoice[] = [
    ...(pinnedLeader ? [pinnedLeader] : []),
    ...members.data.map((member) => ({
      userId: member.userId,
      name: member.fullName,
      email: member.email,
      individual: member.asIndividual(),
    })),
  ];

  function clearSearch() {
    setSearchTerm("");
    setDebouncedSearchTerm("");
  }

  function saveChanges(teamLeader: Individual) {
    onTeamLeaderUpdated?.(teamLeader);
    onPopoverClose?.();
  }

  return (
    <Popover
      anchorEl={popoverAnchor}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={isOpen}
      onClose={() => onPopoverClose?.()}
    >
      <PopoverContainer>
        <SearchField
          placeholder="Search Users"
          slotProps={{
            htmlInput: { maxLength: ENTITY_MEMBER_QUERY_MAXIMUM_LENGTH },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {searchTerm.length > 0 && (
                    <IconButton size="small" aria-label="Clear search" onClick={clearSearch}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </>
              ),
            },
          }}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <ListContainer dense ref={setListElement}>
          {choices.length < 1 && !isInitialLoading && !error && (
            <ListItem>
              <ListItemText
                primary="None Found"
                slotProps={{ primary: { sx: { paddingTop: 2, textAlign: "center", typography: "body1" } } }}
              />
            </ListItem>
          )}
          {choices.map((choice) => (
            <LeaderChoiceRow
              key={choice.userId.value}
              choice={choice}
              isSelected={choice.userId.isEqualTo(teamLeaderUserId)}
              onChoose={saveChanges}
            />
          ))}
          {isLoading && (
            <LoaderRow>
              <CircularProgress size={24} aria-label="Loading members" />
            </LoaderRow>
          )}
          {Boolean(error) && !isLoading && (
            <ErrorRow>
              <ListItemText primary="Error loading members" />
              <Button color="inherit" size="small" onClick={retry}>
                Retry
              </Button>
            </ErrorRow>
          )}
        </ListContainer>
      </PopoverContainer>
    </Popover>
  );
}
